"use client";

import { AnimatePresence, motion } from "framer-motion";
import { CornerUpLeft, MessageCircleMore } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { AgentWorkspace } from "@/components/AgentWorkspace";

const DEFAULT_CHAT_SIZE = { width: 380, height: 520 };
const MIN_CHAT_WIDTH = 340;
const MAX_CHAT_WIDTH = 560;
const MIN_CHAT_HEIGHT = 420;
const MAX_CHAT_HEIGHT = 760;

export function AgentFloatingChat() {
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [panelSize, setPanelSize] = useState(DEFAULT_CHAT_SIZE);
  const resizeStateRef = useRef<{
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    maxWidth: number;
    maxHeight: number;
  } | null>(null);

  const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

  const getViewportBounds = useCallback(() => {
    if (typeof window === "undefined") {
      return {
        maxWidth: MAX_CHAT_WIDTH,
        maxHeight: MAX_CHAT_HEIGHT,
      };
    }

    return {
      maxWidth: Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, window.innerWidth - 24)),
      maxHeight: Math.max(MIN_CHAT_HEIGHT, Math.min(MAX_CHAT_HEIGHT, window.innerHeight - 24)),
    };
  }, []);

  const stopResize = useCallback(() => {
    resizeStateRef.current = null;
    window.removeEventListener("pointermove", handleResize);
    window.removeEventListener("pointerup", stopResize);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, []);

  const handleResize = useCallback((event: PointerEvent) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState) {
      return;
    }

    const nextWidth = clamp(
      resizeState.startWidth - (event.clientX - resizeState.startX),
      MIN_CHAT_WIDTH,
      resizeState.maxWidth,
    );
    const nextHeight = clamp(
      resizeState.startHeight - (event.clientY - resizeState.startY),
      MIN_CHAT_HEIGHT,
      resizeState.maxHeight,
    );

    setPanelSize({
      width: nextWidth,
      height: nextHeight,
    });
  }, []);

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handleResize);
      window.removeEventListener("pointerup", stopResize);
    };
  }, [handleResize, stopResize]);

  useEffect(() => {
    const syncPanelSize = () => {
      const { maxWidth, maxHeight } = getViewportBounds();
      setPanelSize((current) => ({
        width: clamp(current.width, MIN_CHAT_WIDTH, maxWidth),
        height: clamp(current.height, MIN_CHAT_HEIGHT, maxHeight),
      }));
    };

    syncPanelSize();
    window.addEventListener("resize", syncPanelSize);

    return () => window.removeEventListener("resize", syncPanelSize);
  }, [getViewportBounds]);

  const startResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { maxWidth, maxHeight } = getViewportBounds();
    resizeStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: panelSize.width,
      startHeight: panelSize.height,
      maxWidth,
      maxHeight,
    };

    document.body.style.userSelect = "none";
    document.body.style.cursor = "nwse-resize";
    window.addEventListener("pointermove", handleResize);
    window.addEventListener("pointerup", stopResize);
  };

  if (pathname === "/agent") {
    return null;
  }

  return (
    <div className="fixed bottom-5 right-5 z-[70] flex flex-col items-end sm:bottom-6 sm:right-6">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="relative mb-4 overflow-hidden rounded-[1.9rem] border border-white/80 bg-white/92 shadow-[0_30px_80px_rgba(15,23,42,0.18)] backdrop-blur-[24px] dark:border-white/10 dark:bg-[#090b11]/92 dark:shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            style={{
              width: panelSize.width,
              height: panelSize.height,
              maxWidth: "calc(100vw - 1.5rem)",
              maxHeight: "calc(100vh - 1.5rem)",
            }}
          >
            <button
              type="button"
              onPointerDown={startResize}
              className="absolute left-3 top-3 z-20 inline-flex h-7 w-7 items-center justify-center rounded-[10px] border border-[#E2EAF8] bg-white/72 text-[#9AA6BA] shadow-[0_6px_18px_rgba(15,23,42,0.06)] backdrop-blur transition-all hover:border-[#C9D8FF] hover:bg-white/92 hover:text-[#335AFF] dark:border-white/10 dark:bg-[#111827]/72 dark:text-[#7f90ad] dark:hover:border-white/15 dark:hover:bg-[#111827]/92 dark:hover:text-white"
              aria-label="调整窗口大小"
              title="拖拽调整窗口大小"
            >
              <CornerUpLeft className="pointer-events-none h-3.5 w-3.5" strokeWidth={2.1} />
            </button>
            <AgentWorkspace
              variant="popup"
              onMinimize={() => setIsOpen(false)}
              onExpand={() => {
                router.push("/agent");
                setIsOpen(false);
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="group flex h-14 w-14 items-center overflow-hidden rounded-full bg-[#335AFF] pr-5 text-white shadow-[0_20px_48px_rgba(51,90,255,0.32)] transition-all duration-300 hover:w-[142px] hover:bg-[#2848D9]"
        aria-label="问 AI"
      >
        <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center">
          <MessageCircleMore className="h-5 w-5" />
        </span>
        <span className="whitespace-nowrap text-sm font-semibold opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          问 AI
        </span>
      </button>
    </div>
  );
}
